//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  IContentLibraryItem,
  IContentLibrarySourcesOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import HR from '@components/atoms/hr/HR';
import Spinner from '@components/atoms/spinner/Spinner';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { useContentLibraryItemsInfiniteQuery } from '@components/media-filters/useContentLibraryItemsInfiniteQuery';
import { useContentLibraryParamsMapper } from '@components/media-filters/useContentLibraryParamsMapper';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { MediaSourcesDropdown } from '@content-drawer/filters/MediaSourcesDropdown';
import { MediaPanelActions } from '@content-drawer/media/MediaPanelActions';
import {
  CONTENT_DRAWER_MEDIA_LIMIT,
  MediaPanelContent,
} from '@content-drawer/media/MediaPanelContent';
import { MediaPanelHeader } from '@content-drawer/media/MediaPanelHeader';
import { getContentDrawerSettings } from '@content-drawer/selectors/getContentDrawerSettings';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';

export const MEDIA_PANEL_WIDTH = '450px';

export interface MediaPanelProps {
  onChangeFilters: (section: FilterSection<FilterValue>) => void;
  onChangeSources: (value: IContentLibrarySourcesOptions) => void;
  onClose: () => void;
  onResetFilters: () => void;
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>;
  sources: IContentLibrarySourcesOptions;
  toggleFilters: (setter: (state: boolean) => boolean) => void;
}

export const MediaPanel = ({
  onChangeFilters,
  onChangeSources,
  onClose,
  onResetFilters,
  sections,
  sources,
  toggleFilters,
}: MediaPanelProps) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const drawerSettings = useSelector(getContentDrawerSettings);
  const { filters, filtersCount } = useContentLibraryParamsMapper({ sections });
  const [previewItem, setPreviewItem] = useState<IContentLibraryItem>();
  const [selectedItems, setSelectedItems] = useState<
    Map<string, IContentLibraryItem>
  >(new Map());

  const { isFetching } = useContentLibraryItemsInfiniteQuery({
    query: {
      filters,
      limit: CONTENT_DRAWER_MEDIA_LIMIT,
      sources,
    },
  });

  const onSelectItem = (item: IContentLibraryItem) => {
    setSelectedItems((items) => {
      const newState = new Map(items);
      if (newState.has(item.id)) {
        newState.delete(item.id);
        return newState;
      }
      if (drawerSettings.multiSelect) {
        if (newState.size === drawerSettings.multiSelectLimit) {
          dispatch(
            toastNotificationsActions.addToastNotification({
              message: `You can only select up to ${drawerSettings.multiSelectLimit} media items.`,
              options: {
                toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              },
              title: 'Upload Limit Reached',
              type: ToastType.WARNING,
            }),
          );
          return items;
        }
        return newState.set(item.id, item);
      } else {
        return new Map<string, IContentLibraryItem>().set(item.id, item);
      }
    });
  };

  const onToggleFilters = (producer: (state: boolean) => boolean) => {
    toggleFilters((state: boolean) => {
      const toggle = producer(state);
      if (toggle) {
        setPreviewItem(undefined);
      }

      return toggle;
    });
  };

  return (
    <Panel>
      <MediaPanelHeader onClose={onClose} selectedItems={selectedItems} />
      <MediaPanelActions
        filtersCount={filtersCount}
        onClear={() => setSelectedItems(new Map())}
        selectedItemsCount={selectedItems.size}
        toggleFilters={onToggleFilters}
      />
      <HR />
      <MediaPanelSourcesWrapper>
        <MediaSourcesDropdown
          container={ref.current ?? undefined}
          onChangeSources={onChangeSources}
          onChangeUserIds={onChangeFilters}
          searchTrackingIdPrefix={`${ContentDrawerTrackingIds.SEARCH_USERS}-`}
          sources={sources}
          userIds={getSection(sections, 'userIds')}
        />
        {isFetching && (
          <SpinnerWrapper>
            <StyledSpinner />
          </SpinnerWrapper>
        )}
      </MediaPanelSourcesWrapper>
      <MediaPanelContent
        onResetFilters={onResetFilters}
        onPreviewItem={setPreviewItem}
        onSelectItem={onSelectItem}
        previewItem={previewItem}
        sections={sections}
        selectedItems={selectedItems}
        sources={sources}
        toggleFilters={onToggleFilters}
      />
      <div ref={ref} />
    </Panel>
  );
};

const Panel = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: ${MEDIA_PANEL_WIDTH};
    position: relative;
    background: ${theme.colors.white};
    box-shadow: ${theme.boxShadows[3]};
  `,
);

export const MediaPanelSourcesWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    margin: ${theme.spacings[3]} ${theme.spacings[3]} ${theme.spacings[3]} ${theme.spacings[0]};
  `,
);

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSpinner = styled(Spinner)(
  ({ theme }) => `
    width: ${theme.spacings[3]};
    height: ${theme.spacings[3]};
  `,
);
